// notifTarget.mjs — 「点这条通知去哪」的**唯一**判据（第一百零九批 D）。
//
// ── 为什么它从 NotificationBell.jsx 里搬出来 ─────────────────────────────────
// 它原本是那个组件里的一只私有函数，而 `.jsx` 不进单测（SB-042 约定：可判定逻辑入
// `.mjs` 配单测）。第一百零八批 R1 刚为同一件事付过账：判定写在 `.jsx` 里，把它删掉
// 全套测试仍然全绿。本批要往里加一个**带副作用风险**的分支（见下），那个分支若无人
// 看守，坏掉的形式是"点一下铃铛就把信标成已读了"——而用户以为自己只是看了眼提醒。
//
// ── 站内信这一支的两条硬规矩（本批风险 5）────────────────────────────────────
//  ① 目标只能是**列表** `/messages?box=inbox&focus={id}`，**绝不是** `/messages/{id}`。
//     裁定 3 把「点开正文」定义成已读，而已读会给对方发回执、并永久关死撤回窗口。
//     一条把用户直接送进正文的通知，等于替他做了一件不可逆的事。
//  ② 三种 kind（dm_received / dm_read / dm_wait_expired）都落在同一个列表面上，
//     但 box 不同：收到的信在收件箱，"对方读了你的信"与"排队的信没发出"是**我发的**
//     信，在发件箱。跳错箱子的表现是点进去看到一个空列表——而空白同时兼容
//     "一切正常"与"彻底坏了"（SB-120）。
//
// 纯函数、无 DOM、无 React。

// 通知行 → 跳转目标；null = 纯告知，没有任何面还能显示它的对象。
export function targetOf(n) {
  // 已取消的工作项是终局，/work-items 列表永远显示不了它（第四十八批审查）。
  if (n?.kind === 'backlog_cancelled') return null;
  // SB-078 L3：这一条是**给审批人的**，去 /my-tickets 找不到那张单——它多半不是他
  // 自己提交的。收件人集合就是有审批权的账号，所以直达审批页。
  if (n?.kind === 'ruling_requested') return '/approvals';
  // SB-052（2026-08-17）：这两条同理不是收件人自己的单——T0 的收件人是审批人，
  // T6 的收件人是管理员（工作空间操作员的承载），且 T6 后工单回到 pending，
  // 正是审批队列显示的那个状态。incident_reviewed 走下面的缺省 /my-tickets：
  // 它发给的就是提交人自己。
  if (n?.kind === 'ticket_submitted' || n?.kind === 'ticket_requeued') return '/approvals';
  // 第二百三十一批：模型服务的**账户**不让用（余额／配额／密钥）。落点是管理面的
  // 作业面板，因为要做的两件事都在那里：看还有哪些作业倒在同一堵墙上，以及账户恢复
  // 之后把它们重排。落到缺省的 /my-tickets 会把一条基础设施告警送去一个与它无关的
  // 页面——而这条通知存在的全部理由，就是让人**立刻**走到能处理它的那一格。
  if (n?.kind === 'provider_blocked') return '/admin/settings';
  if (n?.entityKind === 'direct_message') return messagesTarget(n);
  // 第一百二十五批 C：组间线的归档四事。**必须带 focus**，因为这一族通知里最贵的
  // 那条是「三日后自动归档」——收到它的人要做的判断是"这条线到底还没完没完"，
  // 而那个判断只有打开**那一条线**才做得出。落到一个不选中任何线的列表页，
  // 他多半就把它当成一条噪音关掉了，然后三天后线自己归了档。
  if (n?.entityKind === 'group_thread') return threadsTarget(n);
  return n?.entityKind === 'backlog' ? '/work-items' : '/my-tickets';
}

// 组间线那一支。entityNo 是线 id 的十进制串（group_thread_store 里 `std::to_string(id)`）。
// 认不出来就只给列表，**不拼一个猜出来的 focus**（同 messagesTarget 的理由）。
// 归档态的线默认不在列表里，所以带 focus 的链接还要 `showArchived=1`——否则
// 「你的线已归档」这条通知会跳到一个**看不见那条线**的页面，而那是最坏的一种落点：
// 它看起来像"这条线没了"。
function threadsTarget(n) {
  const id = String(n.entityNo ?? '');
  if (!/^\d+$/.test(id)) return '/threads';
  const archived = n.kind === 'thread_archived';
  return `/threads?focus=${id}${archived ? '&showArchived=1' : ''}`;
}

// 站内信那一支。entityNo 是那封信的 id 的十进制串（message_store 里
// `std::to_string(id)`）；认不出来就只给列表，**不拼一个猜出来的 focus**——
// 一个指向不存在的行的高亮，比没有高亮更让人以为自己漏看了什么。
function messagesTarget(n) {
  const box = n.kind === 'dm_received' ? 'inbox' : 'outbox';
  const id = String(n.entityNo ?? '');
  const focus = /^\d+$/.test(id) ? `&focus=${id}` : '';
  return `/messages?box=${box}${focus}`;
}
